/**
 * HelpCenter — a tiny, reusable Help / Resources entry point.
 *
 * A button that opens a modal with three actions:
 *   - Start Interactive Tour (emits a global `veridex:start-tour` event
 *     that SimpleDashboard listens to and starts react-joyride)
 *   - Watch Video Tutorial (opens the configured URL in a new tab)
 *   - Read Methodology (routes to /methodology)
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { HelpCircle, PlayCircle, BookOpen, Video, ArrowRight } from 'lucide-react';

// Replace with the real URL once a tutorial exists.
const VIDEO_TUTORIAL_URL = 'https://www.youtube.com/@veridex';

export default function HelpCenter({ variant = 'sidebar' }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const triggerTour = () => {
    setOpen(false);
    // Fire a global event — SimpleDashboard listens for it.
    window.dispatchEvent(new CustomEvent('veridex:start-tour'));
    // If we're not on the simple dashboard, navigate there; the dashboard
    // starts the tour on mount if the flag is set in sessionStorage.
    try { sessionStorage.setItem('veridex:tour-pending', '1'); } catch { /* noop */ }
    if (!window.location.pathname.startsWith('/simple-dashboard')) {
      navigate('/simple-dashboard');
    }
  };

  const TriggerButton = variant === 'sidebar'
    ? (
      <button
        type="button"
        data-testid="help-center-open-btn"
        className="cp-sidebar-link w-full"
        title="Help & tour"
      >
        <HelpCircle className="w-[18px] h-[18px] flex-shrink-0" />
        <span className="truncate">Help &amp; Tour</span>
      </button>
    )
    : (
      <Button variant="outline" size="sm" data-testid="help-center-open-btn">
        <HelpCircle className="w-4 h-4 mr-2" /> Help
      </Button>
    );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="help-center-dialog">
        <DialogHeader>
          <DialogTitle>Need a hand?</DialogTitle>
          <DialogDescription>
            Three quick ways to get comfortable with Veridex One — pick whichever suits you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <button
            type="button"
            onClick={triggerTour}
            data-testid="help-start-tour-btn"
            className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-left"
          >
            <PlayCircle className="w-5 h-5 text-primary shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-sm">Start Interactive Tour</div>
              <div className="text-xs text-muted-foreground">Guided 4-step walkthrough of your dashboard</div>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
          </button>

          <a
            href={VIDEO_TUTORIAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            data-testid="help-video-btn"
            className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-left"
          >
            <Video className="w-5 h-5 text-primary shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-sm">Watch Video Tutorial</div>
              <div className="text-xs text-muted-foreground">2-minute walkthrough of the whole platform</div>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
          </a>

          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/methodology'); }}
            data-testid="help-methodology-btn"
            className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-left"
          >
            <BookOpen className="w-5 h-5 text-primary shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-sm">Read the Methodology</div>
              <div className="text-xs text-muted-foreground">How Veridex calculates PCF &amp; risk, in plain English</div>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
